import React from 'react'

import { useSnackbar } from 'contexts'

import type { CertificateAdmin } from 'types'

import { useTheme, Stack, Typography, Box } from '@mui/material'

import { Modal, Button } from '@repo/ui'
import { CertificatePreview } from 'components'

interface BulkDeleteCertificatesModalProps {
  certificates: CertificateAdmin[]
  totalCount: number
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading: boolean
}

export const BulkDeleteCertificatesModal = (props: BulkDeleteCertificatesModalProps) => {
  const { certificates, totalCount, open, onClose, onConfirm, isLoading } = props
  const theme = useTheme()

  const { open: notification } = useSnackbar()

  const nonDeletable = certificates.filter(c => c.status !== 'inactive')
  const hasNonDeletable = nonDeletable.length > 0

  const handleClose = () => onClose()

  const handleConfirm = () => {
    if (hasNonDeletable) {
      notification({
        type: 'error',
        title: `${nonDeletable.length} selected certificate(s) on this page are not inactive and may not be deleted`,
      })
    }
    onConfirm()
  }

  const visibleCount = certificates.length
  const hiddenCount = Math.max(0, totalCount - visibleCount)

  return (
    <Modal
      title={`Delete ${totalCount} Certificate${totalCount > 1 ? 's' : ''}`}
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            destructive
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : `Delete ${totalCount} Certificate${totalCount > 1 ? 's' : ''}`}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete the selected {totalCount} certificate{totalCount > 1 ? 's' : ''}?
        </Typography>

        {hasNonDeletable && (
          <Box sx={{
            p: 3,
            backgroundColor: theme.palette.error[50],
            border: `1px solid ${theme.palette.error[200]}`,
            borderRadius: 2,
          }}>
            <Typography variant="body2" color="error.main" sx={{ mb: 2 }}>
              ⚠️ {nonDeletable.length} certificate{nonDeletable.length > 1 ? 's' : ''} cannot be deleted (status not inactive):
            </Typography>
            {nonDeletable.slice(0, 3).map((certificate) => (
              <Typography key={certificate.id} variant="body3" color="error.main">
                • {certificate.product?.title} — {certificate.id} ({certificate.status})
              </Typography>
            ))}
            {nonDeletable.length > 3 && (
              <Typography variant="body3" color="error.main">
                • ... and {nonDeletable.length - 3} more on this page
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          <Stack spacing={2}>
            {certificates.slice(0, 5).map((certificate) => (
              <CertificatePreview key={certificate.id} certificate={certificate as any} size="medium" />
            ))}
            {(visibleCount > 5 || hiddenCount > 0) && (
              <Typography variant="body3" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                {visibleCount > 5 && `... and ${visibleCount - 5} more on this page`}
                {visibleCount > 5 && hiddenCount > 0 && ' · '}
                {hiddenCount > 0 && `+ ${hiddenCount} on other page${hiddenCount > 1 ? 's' : ''}`}
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Modal>
  )
}

